/* ============================================================================
   size-guide.js — <sf-size-guide> (Size Guide page + PDP sheet). Figma 1.7.
   Markup: snippets/size-guide-tool.liquid. Data and maths: window.SFSizing
   (assets/sizing.js — the same rows as assets/size-charts.json, or the shop
   metafield custom.size_charts when it is set). Nothing here holds a number.

   Shoes   intro naming the chart actually shown (brand + gender from the
           data) → EU / UK / US M / US W / CM table (a column only when every
           row has it) with the selected size's row highlighted → source line
           → "Measure your foot" / "Compare a shoe you own" open in place.
   Apparel Tops | Bottoms body chart (only the measurements the data holds)
           → how to measure, showing only the measurements in the chart.

   Units: the cm | in choice is saved at theme.storage 'units' (localStorage
   'sf:units', {measure:'cm'|'in'}) and shared by every instance; a change is
   broadcast as document CustomEvent 'sf:unitchange' {system, measure} and
   theme.pubsub 'units:change'. The size SYSTEM the results print in is the
   merchant's settings.size_default_system (EU unless changed) — there is no
   size-system toggle anywhere (FIX 08: EU sizing only on the product page and
   in quick add), so a system saved by an older theme version is ignored rather
   than leaving results in UK with no way back.

   Select-size hook (product context): "Select 44 1/2 EU" publishes
   theme.pubsub 'sizeguide:select' {eu, handle, variantId} + document
   CustomEvent 'sizeguide:select' (product-form.js checks that radio), closes
   the enclosing sf-drawer and toasts. `eu` is the variant's own option value.

   Analytics: theme.track(name, payload) when it exists, else no-op.
   Events: size_guide_tab, size_guide_result, size_guide_compare,
   size_guide_measure, size_selected_from_guide.
   ============================================================================ */
(() => {
  'use strict';
  if (customElements.get('sf-size-guide')) return;

  const SYSTEMS = ['eu', 'uk', 'us-m', 'us-w'];
  const MEASURES = ['cm', 'in'];
  const SYSTEM_KEY = { eu: 'eu', uk: 'uk', 'us-m': 'us_m', 'us-w': 'us_w' };
  const ROW_KEY = { eu: 'eu', uk: 'uk', 'us-m': 'usM', 'us-w': 'usW' };
  const APPAREL_ALIAS = { '2XL': 'XXL', '3XL': 'XXXL', '2XS': 'XXS', XXXS: '3XS' };

  const T = () => window.theme || {};
  const S = () => window.SFSizing || window.SF_SIZING || null;
  const esc = (s) => (T().escapeHtml ? T().escapeHtml(String(s == null ? '' : s)) : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]));
  const track = (name, payload) => {
    const t = T();
    if (typeof t.track === 'function') {
      try { t.track(name, payload || {}); } catch (e) { /* ignore */ }
    }
  };
  const titleCase = (s) => String(s || '').replace(/(^|\s)\S/g, (m) => m.toUpperCase());
  const fmt = (tpl, vars) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (vars && k in vars ? vars[k] : m));
  const num = (n) => (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');

  /* ---- units (shared by every instance on the page) ---- */
  const units = {
    state: null,
    read(defaultSystem) {
      if (this.state) return this.state;
      const saved = (T().storage && T().storage.get('units', null)) || null;
      const st = { system: SYSTEMS.includes(defaultSystem) ? defaultSystem : 'eu', measure: 'cm' };
      if (saved && typeof saved === 'object' && MEASURES.includes(saved.measure)) st.measure = saved.measure;
      this.state = st;
      return st;
    },
    set(patch) {
      const st = this.read();
      let changed = false;
      if (patch.system && SYSTEMS.includes(patch.system) && patch.system !== st.system) { st.system = patch.system; changed = true; }
      if (patch.measure && MEASURES.includes(patch.measure) && patch.measure !== st.measure) { st.measure = patch.measure; changed = true; }
      if (!changed) return false;
      if (T().storage) T().storage.set('units', Object.assign({}, T().storage.get('units', null) || {}, { measure: st.measure }));
      const detail = { system: st.system, measure: st.measure, source: 'size-guide' };
      document.dispatchEvent(new CustomEvent('sf:unitchange', { detail }));
      if (T().pubsub) T().pubsub.publish('units:change', detail);
      return true;
    },
    /* another instance changed cm | in: adopt, never re-broadcast */
    sync(detail) {
      const st = this.read();
      if (detail && MEASURES.includes(detail.measure)) st.measure = detail.measure;
    },
    systemLabel(strings, id) {
      const key = SYSTEM_KEY[id || this.read().system] || 'eu';
      return (strings && strings[key]) || key.toUpperCase().replace('_', ' ');
    },
    formatLength(strings, cm, bare) {
      if (cm == null || !isFinite(cm)) return '—';
      const st = this.read();
      const sizing = S();
      if (st.measure === 'in' && sizing && sizing.cmToIn) {
        const inches = Math.round(sizing.cmToIn(cm) * 10) / 10;
        return inches.toFixed(1) + (bare ? '' : ' ' + ((strings && strings.in) || 'in'));
      }
      return (Math.round(cm * 10) / 10).toFixed(1) + (bare ? '' : ' ' + ((strings && strings.cm) || 'cm'));
    },
    /* a body measurement (number or [min, max]) in the current unit, no unit suffix */
    formatBody(v) {
      const sizing = S();
      const inch = this.read().measure === 'in' && sizing && sizing.cmToIn;
      const one = (x) => (inch ? num(sizing.cmToIn(x)) : String(Math.round(x * 10) / 10));
      return Array.isArray(v) ? one(v[0]) + '–' + one(v[1]) : one(v);
    }
  };

  /* Shop-wide chart override (metafield). Re-applied lazily because another
     feature file may load sizing.js a second time and reset the built-ins. */
  let chartsJson = null;
  function ensureCharts(root) {
    const sizing = S();
    if (!sizing) return;
    if (chartsJson === null) {
      const node = root.querySelector('[data-size-charts]') || document.querySelector('[data-size-charts]');
      chartsJson = node ? node.textContent.trim() : '';
    }
    if (!chartsJson || typeof sizing.setCharts !== 'function') return;
    if (sizing.__sfChartsApplied === chartsJson) return;
    if (sizing.setCharts(chartsJson)) sizing.__sfChartsApplied = chartsJson;
  }

  class SFSizeGuide extends HTMLElement {
    connectedCallback() {
      this._ctrl = new AbortController();
      const signal = this._ctrl.signal;
      this.strings = this._json('[data-sg-strings]', {});
      this.sizes = this._json('[data-sg-sizes]', null);
      this.current = this._json('[data-sg-current]', null);
      this.brand = (this.dataset.brand || '').toLowerCase().trim();
      this.brandName = (this.dataset.brandName || '').trim();
      this.kind = this.dataset.kind || 'all';
      this.context = this.dataset.context || 'page';
      this.sizeIndex = parseInt(this.dataset.sizeIndex, 10);
      this.selectedEu = this.dataset.selectedEu || '';
      units.read(this.dataset.defaultSystem);

      this.brandSel = this.querySelector('[data-sg-brand]');
      this.sizeSel = this.querySelector('[data-sg-size]');
      this.footInput = this.querySelector('[data-sg-foot]');
      this.footUnit = this.querySelector('[data-sg-foot-unit]');
      this.tableHost = this.querySelector('[data-sg-table]');
      this.picker = this.querySelector('[data-sg-brand-picker]');
      this.intro = this.querySelector('[data-sg-intro]');
      this.sourceNote = this.querySelector('[data-sg-source]');
      this.selectedNote = this.querySelector('[data-sg-selected-note]');
      this.apparelNote = this.querySelector('[data-sg-apparel-note]');
      this.panes = {
        compare: this.querySelector('[data-sg-pane="compare"]'),
        measure: this.querySelector('[data-sg-pane="measure"]')
      };

      const boot = () => {
        if (!S()) { this._waitForSizing(boot); return; }
        ensureCharts(this);
        this.paintUnits();
        this.fillBrands();
        this.fillSizes();
        this.syncFootUnit();
        this.paintChart();
        this.paintApparel();
      };
      boot();
      this.syncKinds();

      /* sub tabs (sf-tabs owns keyboard + aria; this only repaints what depends on the active tab) */
      this.addEventListener('tabs:change', (e) => {
        const id = (e.detail && e.detail.id) || '';
        const tab = id.split('-').pop();
        if (e.target && e.target.classList && e.target.classList.contains('sg-kinds')) this.syncKinds();
        if (tab === 'tops' || tab === 'bottoms') this.paintHowTo();
        track('size_guide_tab', { tab, surface: this.context });
      }, { signal });

      this.addEventListener('click', (e) => {
        const opt = e.target.closest('[data-unit-value]');
        if (opt && this.contains(opt)) {
          const group = opt.closest('[data-unit-toggle]');
          const kind = group && group.dataset.unitToggle === 'measure' ? 'measure' : 'system';
          if (units.set({ [kind]: opt.dataset.unitValue })) this.repaintUnits();
          return;
        }
        const open = e.target.closest('[data-sg-open]');
        if (open && this.contains(open)) { e.preventDefault(); this.openView(open.dataset.sgOpen, open); return; }
        const back = e.target.closest('[data-sg-back]');
        if (back && this.contains(back)) { e.preventDefault(); this.closeView(); return; }
        const check = e.target.closest('[data-sg-check]');
        if (check && this.contains(check)) {
          e.preventDefault();
          if (check.dataset.sgCheck === 'compare') this.runCompare();
          else this.runMeasure();
          return;
        }
        const brandBtn = e.target.closest('[data-sg-brand-picker] [data-brand]');
        if (brandBtn && this.contains(brandBtn)) {
          if (this.tableHost) this.tableHost.dataset.brand = brandBtn.dataset.brand;
          this.paintChart();
          return;
        }
        const sel = e.target.closest('[data-sg-select]');
        if (sel && this.contains(sel) && !sel.disabled) {
          e.preventDefault();
          this.selectSize(sel.dataset.eu, sel.dataset.variantId);
        }
      }, { signal });

      /* arrow keys inside a unit radiogroup (roving tabindex) */
      this.addEventListener('keydown', (e) => {
        const opt = e.target.closest && e.target.closest('[data-unit-value]');
        if (!opt || !this.contains(opt)) return;
        const rtl = !!T().rtl || document.documentElement.dir === 'rtl';
        const fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
        const back = rtl ? 'ArrowRight' : 'ArrowLeft';
        const dir = e.key === fwd || e.key === 'ArrowDown' ? 1 : e.key === back || e.key === 'ArrowUp' ? -1 : 0;
        if (!dir) return;
        e.preventDefault();
        const group = opt.closest('[data-unit-toggle]');
        const all = Array.from(group.querySelectorAll('[data-unit-value]'));
        const next = all[(all.indexOf(opt) + dir + all.length) % all.length];
        const kind = group.dataset.unitToggle === 'measure' ? 'measure' : 'system';
        if (units.set({ [kind]: next.dataset.unitValue })) this.repaintUnits();
        next.focus();
      }, { signal });

      if (this.brandSel) {
        this.brandSel.addEventListener('change', () => { this.fillSizes(); this.clearResult('compare'); }, { signal });
      }
      if (this.footInput) {
        this.footInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') { e.preventDefault(); this.runMeasure(); }
        }, { signal });
      }

      /* another instance changed cm | in */
      document.addEventListener('sf:unitchange', (e) => {
        units.sync(e.detail);
        this.repaintUnits();
      }, { signal });

      /* the sheet always opens on the chart, never on a sub view left open last time */
      const drawer = this.closest('sf-drawer');
      if (drawer) drawer.addEventListener('drawer:open', () => this.showView('chart'), { signal });

      /* PDP variant change → move the highlighted row and the stock check to the new size / colour */
      if (this.dataset.productHandle && T().pubsub) {
        this._unsub = T().pubsub.subscribe('variant:change', (payload) => {
          const v = payload && payload.variant;
          if (!v || !Array.isArray(this.sizes) || !this.sizes.some((s) => String(s.id) === String(v.id))) return;
          const opts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter((o) => o != null);
          this.current = opts;
          const hit = isFinite(this.sizeIndex) && this.sizeIndex > -1 ? opts[this.sizeIndex] : opts.find((o) => this.allSizes().includes(String(o)));
          if (hit != null) this.selectedEu = String(hit);
          this.paintChart();
          this.paintApparel();
        });
      }
    }

    disconnectedCallback() {
      if (this._ctrl) this._ctrl.abort();
      if (this._unsub) this._unsub();
      if (this._poll) clearInterval(this._poll);
    }

    _waitForSizing(cb) {
      if (this._poll) return;
      let tries = 0;
      this._poll = setInterval(() => {
        tries += 1;
        if (S() || tries > 100) {
          clearInterval(this._poll);
          this._poll = null;
          if (S()) cb();
        }
      }, 50);
    }

    _json(sel, fallback) {
      const node = this.querySelector(sel);
      if (!node) return fallback;
      try { return JSON.parse(node.textContent); } catch (e) { return fallback; }
    }

    t(key, vars) { return fmt(this.strings[key] || '', vars); }
    /* the same string as HTML, each inserted value isolated in <bdi> so "44 1/2 EU" keeps its order in Arabic */
    tHtml(key, vars) {
      const wrapped = {};
      Object.keys(vars || {}).forEach((k) => { wrapped[k] = '<bdi>' + esc(vars[k]) + '</bdi>'; });
      return fmt(esc(this.strings[key] || ''), wrapped);
    }

    /* ---- page: Shoes | Apparel switch (sf-tabs on the .seg control) ---- */
    syncKinds() {
      const seg = this.querySelector('.sg-kinds');
      if (!seg) return;
      const tabs = Array.from(seg.querySelectorAll('[data-tab]'));
      const i = Math.max(0, tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      seg.setAttribute('data-active', String(i));
    }

    /* ---- in-place views: chart ⇄ measure / compare ---- */
    showView(name) {
      const shoes = this.querySelector('[data-sg-section="shoes"]');
      if (!shoes) return null;
      let shown = null;
      shoes.querySelectorAll('[data-sg-view]').forEach((v) => {
        const on = v.dataset.sgView === name;
        v.hidden = !on;
        if (on) shown = v;
      });
      return shown;
    }
    openView(name, opener) {
      const view = this.showView(name);
      if (!view) return;
      this._viewOpener = opener || null;
      const body = this.closest('.drawer__body');
      if (body) body.scrollTop = 0;
      else view.scrollIntoView({ block: 'start', behavior: 'auto' });
      const title = view.querySelector('[data-sg-pane-title]');
      if (title) title.focus({ preventScroll: true });
      track('size_guide_tab', { tab: name, surface: this.context });
    }
    closeView() {
      this.showView('chart');
      const back = this._viewOpener;
      this._viewOpener = null;
      if (back && back.isConnected) back.focus({ preventScroll: false });
    }

    /* ---- units UI ---- */
    paintUnits() {
      const st = units.read();
      this.querySelectorAll('[data-unit-toggle]').forEach((group) => {
        const kind = group.dataset.unitToggle === 'measure' ? 'measure' : 'system';
        const opts = Array.from(group.querySelectorAll('[data-unit-value]'));
        opts.forEach((o, i) => {
          const on = o.dataset.unitValue === st[kind];
          o.classList.toggle('is-active', on);
          o.setAttribute('aria-checked', on ? 'true' : 'false');
          o.setAttribute('tabindex', on ? '0' : '-1');
          if (on) group.setAttribute('data-active', String(i));
        });
      });
    }
    repaintUnits() {
      this.paintUnits();
      this.fillSizes();
      this.syncFootUnit();
      this.clearResult('compare');
      this.clearResult('measure');
      this.paintChart();
      this.paintApparel();
    }

    syncFootUnit() {
      const st = units.read();
      if (this.footUnit) this.footUnit.textContent = this.t(st.measure) || st.measure;
      if (this.footInput) {
        this.footInput.setAttribute('placeholder', this.t(st.measure === 'in' ? 'foot_placeholder_in' : 'foot_placeholder_cm'));
        this.footInput.setAttribute('aria-label', this.t(st.measure === 'in' ? 'foot_aria_in' : 'foot_aria_cm'));
      }
    }

    /* ---- the product's sizes (colour-aware: only variants of the colour on screen) ---- */
    sameColour(entry) {
      if (!Array.isArray(this.current) || !Array.isArray(entry.o)) return true;
      return entry.o.every((v, i) => i === this.sizeIndex || this.current[i] == null || String(v) === String(this.current[i]));
    }
    allSizes() {
      if (!Array.isArray(this.sizes)) return [];
      const out = [];
      this.sizes.forEach((s) => { const k = String(s.eu); if (!out.includes(k)) out.push(k); });
      return out;
    }
    availableSizes() { return Array.isArray(this.sizes) ? this.sizes.filter((s) => s.available && this.sameColour(s)).map((s) => String(s.eu)) : []; }
    variantFor(eu) {
      const hit = Array.isArray(this.sizes) ? this.sizes.find((s) => String(s.eu) === String(eu) && s.available && this.sameColour(s)) : null;
      return hit ? hit.id : null;
    }
    hasProduct() { return Array.isArray(this.sizes) && this.sizes.length > 0 && this.kind === 'shoes'; }

    /* "44 1/2 EU" as the shopper's chosen system shows it; only published rows convert */
    displaySize(label) {
      const sizing = S();
      const st = units.read();
      if (st.system !== 'eu' && sizing && sizing.convertLabel) {
        const conv = sizing.convertLabel(this.targetBrand(), label, st.system);
        if (conv && conv.exact && conv.value != null && isFinite(conv.value)) return units.systemLabel(this.strings, st.system) + ' ' + conv.value;
      }
      const p = sizing && sizing.parseSize ? sizing.parseSize(label) : { system: null };
      return p.system ? String(label) : units.systemLabel(this.strings, 'eu') + ' ' + label;
    }

    /* ---- compare: brand + size selects ---- */
    fillBrands() {
      const sizing = S();
      if (!this.brandSel || !sizing) return;
      const current = this.brandSel.value || this.brand;
      const brands = Array.isArray(sizing.brands) ? sizing.brands : [];
      if (!brands.length) return;
      this.brandSel.innerHTML = brands.map((b) => `<option value="${esc(b)}">${esc(titleCase(b))}</option>`).join('');
      this.brandSel.value = brands.includes(current) ? current : brands.includes(this.brand) ? this.brand : brands[0];
    }

    fillSizes() {
      const sizing = S();
      if (!this.sizeSel || !this.brandSel || !sizing) return;
      const brand = this.brandSel.value;
      const st = units.read();
      const rows = sizing.table(brand) || [];
      const prev = this.sizeSel.value;
      const seen = {};
      const sys = units.systemLabel(this.strings, st.system);
      this.sizeSel.innerHTML = rows.map((r) => {
        const shown = st.system === 'eu' ? r.eu : r[ROW_KEY[st.system]];
        if (shown == null || seen[shown]) return '';
        seen[shown] = 1;
        return `<option value="${esc(r.eu)}">${esc(sys + ' ' + shown)}</option>`;
      }).join('');
      if (prev && Array.from(this.sizeSel.options).some((o) => o.value === prev)) this.sizeSel.value = prev;
      else if (this.sizeSel.options.length) this.sizeSel.selectedIndex = Math.floor(this.sizeSel.options.length / 2);
    }

    /* ---- errors / results ---- */
    pane(kind) { return this.panes[kind]; }
    showError(kind, msg) {
      const pane = this.pane(kind);
      const el = pane && pane.querySelector('[data-sg-error]');
      if (!el) return;
      if (msg) {
        el.innerHTML = `<svg class="icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg><span>${esc(msg)}</span>`;
        el.hidden = false;
      } else {
        el.textContent = '';
        el.hidden = true;
      }
    }
    clearResult(kind) {
      this.clearResultOnly(kind);
      this.showError(kind, '');
    }
    clearResultOnly(kind) {
      const pane = this.pane(kind);
      const res = pane && pane.querySelector('[data-sg-result]');
      if (res) res.hidden = true;
    }

    /**
     * @param {'compare'|'measure'} kind
     * @param {{size:string, footCm:number, easeCm?:number, approximate?:boolean, aboveRange?:boolean}} res
     * @param {string} [whyHtml]
     */
    renderResult(kind, res, whyHtml) {
      const sizing = S();
      const pane = this.pane(kind);
      const host = pane && pane.querySelector('[data-sg-result]');
      if (!host || !res || !res.size) return;
      const st = units.read();
      const shown = this.displaySize(res.size);
      const len = units.formatLength(this.strings, res.footCm);

      const sizeEl = host.querySelector('[data-sg-result-size]');
      if (sizeEl) sizeEl.innerHTML = `<bdi>${esc(shown)}</bdi>${res.approximate ? `<em class="sg-approx">${esc(this.t('approximate'))}</em>` : ''}`;

      const noteEl = host.querySelector('[data-sg-result-note]');
      if (noteEl) {
        let note = this.t('result_note', { length: len });
        if (st.system !== 'eu' && shown.indexOf(String(res.size)) === -1) note += ' ' + this.t('recorded_as', { size: res.size });
        if (res.easeCm > 0 && kind === 'measure') note += ' ' + this.t('ease', { length: units.formatLength(this.strings, res.easeCm) });
        noteEl.textContent = note;
      }

      const whyEl = host.querySelector('[data-sg-result-why]');
      if (whyEl) { whyEl.innerHTML = whyHtml || ''; whyEl.hidden = !whyHtml; }

      const stockEl = host.querySelector('[data-sg-result-stock]');
      const selectBtn = host.querySelector('[data-sg-select]');
      if (this.hasProduct() && stockEl) {
        const stock = sizing.checkAvailability(res.size, this.availableSizes());
        let line = ''; /* HTML: every branch escapes */
        let cls = '';
        if (res.aboveRange) { line = esc(this.t('above_range')); cls = 'is-out'; }
        else if (stock.known && stock.inStock) { line = esc(this.t('in_stock')); cls = 'is-in'; }
        else if (stock.known) { line = stock.nearest ? this.tHtml('out_of_stock_nearest', { size: this.displaySize(stock.nearest) }) : esc(this.t('out_of_stock')); cls = 'is-out'; }
        stockEl.innerHTML = line;
        stockEl.className = 'sg-stock' + (cls ? ' ' + cls : '');
        stockEl.hidden = !line;
        if (selectBtn) {
          const selectable = stock.known && stock.inStock && !res.aboveRange;
          selectBtn.innerHTML = this.tHtml('select_size', { system: '', size: shown });
          selectBtn.dataset.eu = res.size;
          const vid = this.variantFor(res.size);
          if (vid) selectBtn.dataset.variantId = vid; else delete selectBtn.dataset.variantId;
          selectBtn.disabled = !selectable;
          selectBtn.hidden = false;
        }
      } else {
        if (stockEl) stockEl.hidden = true;
        if (selectBtn) selectBtn.hidden = true;
      }

      host.hidden = false;
      track('size_guide_result', { cm: res.footCm, size: res.size, surface: this.context, flow: kind });
    }

    /* the brand whose chart answers: the shown table's brand (page) or the product's vendor */
    targetBrand() {
      const sizing = S();
      const b = (this.tableHost && this.tableHost.dataset.brand) || this.brand;
      if (b && sizing && sizing.hasChart(b)) return b;
      return b || 'generic';
    }

    /* ---- flow: compare a shoe you own ---- */
    runCompare() {
      const sizing = S();
      if (!sizing || !this.brandSel || !this.sizeSel) return;
      this.showError('compare', '');
      const fromBrand = this.brandSel.value;
      const eu = this.sizeSel.value;
      if (!eu) { this.showError('compare', this.t('err_pair')); this.clearResultOnly('compare'); return; }
      const footCm = sizing.toCm(fromBrand, 'eu', eu);
      if (footCm == null) { this.showError('compare', this.t('err_no_size', { brand: titleCase(fromBrand) })); this.clearResultOnly('compare'); return; }
      const target = this.targetBrand();
      let res;
      if (this.hasProduct()) {
        res = sizing.recommend(target, footCm, this.allSizes());
      } else {
        const row = sizing.fromCm(target, footCm);
        res = row && row.eu != null ? { ok: true, size: String(row.eu), footCm, easeCm: row.easeCm || 0, approximate: !sizing.hasChart(target) } : null;
      }
      if (!res || !res.ok) { this.showError('compare', this.t('err_no_match')); this.clearResultOnly('compare'); return; }
      res.footCm = res.footCm != null ? res.footCm : footCm;

      const label = `${titleCase(fromBrand)} ${units.systemLabel(this.strings, 'eu')} ${eu}`;
      const len = units.formatLength(this.strings, footCm);
      const same = sizing.euValue && Math.abs((sizing.euValue(res.size) || 0) - (sizing.euValue(eu) || 0)) < 0.2;
      const shown = this.displaySize(res.size);
      const why = !same
        ? `<b>${esc(this.t('why_changed_title'))}</b>${esc(this.t('why_changed', { label, length: len, size: shown }))}`
        : `<b>${esc(this.t('why_same_title'))}</b>${esc(this.t('why_same', { label, size: shown }))}`;
      this.renderResult('compare', res, why);
      track('size_guide_compare', { fromBrand, toBrand: target, result: res.size, surface: this.context });
    }

    /* ---- flow: measure your foot ---- */
    runMeasure() {
      const sizing = S();
      if (!sizing || !this.footInput) return;
      this.showError('measure', '');
      const raw = this.footInput.value;
      const unit = units.read().measure;
      if (!String(raw).trim()) {
        this.showError('measure', this.t(unit === 'in' ? 'err_empty_in' : 'err_empty_cm'));
        this.clearResultOnly('measure');
        return;
      }
      const footCm = sizing.parseLength(raw, unit);
      if (footCm == null) {
        this.showError('measure', this.t(unit === 'in' ? 'err_invalid_in' : 'err_invalid_cm'));
        this.clearResultOnly('measure');
        return;
      }
      const lim = sizing.limits || { minCm: 20, maxCm: 33 };
      if (footCm < lim.minCm || footCm > lim.maxCm) {
        this.showError('measure', this.t('err_range', {
          min: units.formatLength(this.strings, lim.minCm),
          max: units.formatLength(this.strings, lim.maxCm)
        }));
        this.clearResultOnly('measure');
        return;
      }
      const target = this.targetBrand();
      let res;
      if (this.hasProduct()) {
        res = sizing.recommend(target, footCm, this.allSizes());
      } else {
        const row = sizing.fromCm(target, footCm);
        res = row && row.eu != null ? { ok: true, size: String(row.eu), footCm, easeCm: row.easeCm || 0, approximate: !sizing.hasChart(target) } : null;
      }
      if (!res || !res.ok) { this.showError('measure', this.t('err_no_measure')); this.clearResultOnly('measure'); return; }
      res.footCm = res.footCm != null ? res.footCm : footCm;
      this.renderResult('measure', res, '');
      track('size_guide_measure', { unit, footCm: res.footCm, result: res.size, surface: this.context });
    }

    /* ---- "Select 44 1/2 EU" → the PDP ---- */
    selectSize(eu, variantId) {
      if (!eu) return;
      const detail = { eu: String(eu), handle: this.dataset.productHandle || '', variantId: variantId ? Number(variantId) : null };
      if (T().pubsub) T().pubsub.publish('sizeguide:select', detail);
      document.dispatchEvent(new CustomEvent('sizeguide:select', { bubbles: true, detail }));
      track('size_selected_from_guide', { size: detail.eu, surface: this.context });
      const drawer = this.closest('sf-drawer, sf-modal');
      if (drawer && typeof drawer.close === 'function') drawer.close();
      if (T().toast) T().toast(this.t('selected_toast', { size: this.displaySize(detail.eu) }));
    }

    /* ---- shoes: intro + chart ---- */
    paintIntro(brand) {
      const sizing = S();
      if (!this.intro || !sizing) return;
      const has = !!brand && sizing.hasChart(brand);
      const name = this.brandName && brand === this.brand ? this.brandName : titleCase(brand);
      let text = '';
      if (has) {
        const g = typeof sizing.gender === 'function' ? sizing.gender() : null;
        text = (g && this.t('intro_' + g, { brand: name })) || this.t('intro_plain', { brand: name });
      } else {
        text = name ? this.t('intro_generic', { brand: name }) : this.t('intro_generic_plain');
      }
      this.intro.textContent = text;
      this.intro.hidden = !text;
    }

    paintChart() {
      const sizing = S();
      if (!this.tableHost || !sizing) return;
      const brand = (this.tableHost.dataset.brand || this.brand || '').toLowerCase();
      const st = units.read();
      const rows = sizing.table(brand || 'generic') || [];
      const has = !!brand && sizing.hasChart(brand);
      this.paintIntro(brand);

      const finite = (k) => rows.length > 0 && rows.every((r) => r[k] != null && isFinite(r[k]));
      const cmLabel = st.measure === 'in' ? (this.t('in') || 'in') : (this.t('cm') || 'cm');
      const cmLong = st.measure === 'in' ? (this.t('col_foot_in') || 'Foot (in)') : (this.t('col_foot_cm') || 'Foot (cm)');
      const cols = [
        { key: 'uk', label: this.t('col_uk') || 'UK' },
        { key: 'usM', label: this.t('col_us_m') || 'US M' },
        { key: 'usW', label: this.t('col_us_w') || 'US W' },
        { key: 'cm', label: cmLabel, long: cmLong }
      ].filter((c) => finite(c.key));
      const name = has ? (this.brandName && brand === this.brand ? this.brandName : titleCase(brand)) : '';
      const caption = has ? this.t('caption', { brand: name }) : this.t('no_chart_note');
      const head = `<th scope="col">${esc(this.t('col_eu') || 'EU')}</th>` + cols.map((c) => (c.long
        ? `<th scope="col"><span aria-hidden="true">${esc(c.label)}</span><span class="sr-only">${esc(c.long)}</span></th>`
        : `<th scope="col">${esc(c.label)}</th>`)).join('');
      const body = rows.map((r, i) => {
        const cells = cols.map((c) => `<td>${esc(c.key === 'cm' ? units.formatLength(this.strings, r.cm, true) : r[c.key])}</td>`).join('');
        return `<tr data-index="${i}" data-eu="${esc(r.eu)}"><th scope="row">${esc(r.eu)}</th>${cells}</tr>`;
      }).join('');
      this.tableHost.innerHTML = `<div class="sg-table__scroll"><table class="sg-table sg-table--shoes"><caption class="sr-only">${esc(caption)}</caption><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
      this.markSelectedRow(brand || 'generic');

      if (this.picker) {
        const brands = Array.isArray(sizing.brands) ? sizing.brands : [];
        this.picker.innerHTML = brands.map((b) => {
          const on = b === brand;
          return `<button type="button" class="chip sg-brand${on ? ' is-on' : ''}" data-brand="${esc(b)}" aria-pressed="${on ? 'true' : 'false'}">${esc(titleCase(b))}</button>`;
        }).join('');
      }
      if (this.sourceNote) {
        this.sourceNote.textContent = has ? this.t('source') : this.t('no_chart_note');
      }
    }

    markSelectedRow(brand) {
      const sizing = S();
      const label = String(this.selectedEu || '').trim();
      if (this.selectedNote) { this.selectedNote.hidden = true; this.selectedNote.textContent = ''; }
      if (!label || !this.tableHost || !sizing || this.kind !== 'shoes') return;
      const row = sizing.rowFor ? sizing.rowFor(brand, label) : null;
      const tr = row ? this.tableHost.querySelector(`tr[data-index="${row.index}"]`) : null;
      if (!tr) {
        /* never point at a neighbouring row: say plainly the size is not on this chart */
        if (this.selectedNote) {
          this.selectedNote.innerHTML = this.tHtml('not_on_chart', { size: label });
          this.selectedNote.hidden = false;
        }
        return;
      }
      tr.classList.add('is-selected');
      tr.setAttribute('aria-current', 'true');
      const th = tr.querySelector('th');
      if (th) th.insertAdjacentHTML('beforeend', `<span class="sr-only"> — ${esc(this.t('selected'))}</span>`);
    }

    /* ---- apparel: Tops | Bottoms body charts ---- */
    paintApparel() {
      const sizing = S();
      if (!sizing || typeof sizing.apparel !== 'function') return;
      const st = units.read();
      this.querySelectorAll('[data-sg-apparel]').forEach((host) => {
        const view = host.dataset.sgApparel;
        const d = sizing.apparel(view);
        if (!d || !d.columns.length || !d.rows.length) { host.innerHTML = ''; return; }
        const head = `<th scope="col">${esc(this.t('col_size') || 'Size')}</th>` + d.columns.map((c) => `<th scope="col">${esc(this.t('col_' + c) || c)}</th>`).join('');
        const body = d.rows.map((r) => `<tr data-size="${esc(r.size)}"><th scope="row">${esc(r.size)}</th>${r.values.map((v) => `<td><span dir="ltr">${esc(units.formatBody(v))}</span></td>`).join('')}</tr>`).join('');
        const caption = this.t(st.measure === 'in' ? 'apparel_caption_in' : 'apparel_caption_cm');
        host.innerHTML = `<p class="sg-unit-note" aria-hidden="true">${esc(caption)}</p><div class="sg-table__scroll"><table class="sg-table sg-table--apparel" data-columns="${esc(d.columns.join(' '))}"><caption class="sr-only">${esc(caption)}</caption><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`;
      });
      this.markApparelRow();
      this.paintHowTo();
    }

    markApparelRow() {
      if (this.apparelNote) { this.apparelNote.hidden = true; this.apparelNote.textContent = ''; }
      const label = String(this.selectedEu || '').trim();
      if (!label || (this.kind !== 'tops' && this.kind !== 'bottoms')) return;
      const norm = (s) => { const k = String(s).toUpperCase().replace(/\s+/g, ''); return APPAREL_ALIAS[k] || k; };
      const want = norm(label);
      let found = false;
      this.querySelectorAll('[data-sg-apparel] tbody tr').forEach((tr) => {
        if (norm(tr.dataset.size) !== want) return;
        found = true;
        tr.classList.add('is-selected');
        tr.setAttribute('aria-current', 'true');
        const th = tr.querySelector('th');
        if (th) th.insertAdjacentHTML('beforeend', `<span class="sr-only"> — ${esc(this.t('selected'))}</span>`);
      });
      if (!found && this.apparelNote) {
        this.apparelNote.innerHTML = this.tHtml('not_on_chart', { size: label });
        this.apparelNote.hidden = false;
      }
    }

    /* how to measure: only the measurements the visible chart actually has */
    paintHowTo() {
      const list = this.querySelector('.sg-howto__list');
      if (!list) return;
      const visible = Array.from(this.querySelectorAll('[data-sg-apparel]')).find((h) => !h.hidden);
      const table = visible && visible.querySelector('table[data-columns]');
      if (!table) return; /* the page's merchant table: keep every line */
      const cols = table.dataset.columns.split(' ');
      list.querySelectorAll('[data-measure]').forEach((li) => { li.hidden = !cols.includes(li.dataset.measure); });
    }
  }

  customElements.define('sf-size-guide', SFSizeGuide);
})();
